import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Package, Users, ShoppingBag, TrendingUp, Save,
} from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = [
  { key: "polymers", label: "Polymers" },
  { key: "chemicals", label: "Chemicals" },
  { key: "cpw_oils", label: "CPW Oils" },
];

const EMPTY_PRODUCT = {
  name: "",
  category: "polymers",
  sku: "",
  price: 0,
  unit: "kg",
  stock: 0,
  moq: 1,
  image: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?w=800",
  description: "",
  specs: {},
};

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | product object | 'new'
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [pRes, sRes] = await Promise.all([
      api.get("/admin/products"),
      api.get("/admin/stats"),
    ]);
    setProducts(pRes.data);
    setStats(sRes.data);
    setLoading(false);
  };

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin")
    return (
      <div className="min-h-screen bg-zinc-100 p-10 flex items-center justify-center">
        <div className="max-w-md bg-white border-2 border-zinc-900 p-8 text-center">
          <div className="tag">ACCESS DENIED</div>
          <h2 className="font-display text-2xl font-black mt-2">Admins only</h2>
          <p className="text-zinc-500 mt-2">
            Your account does not have admin permissions.
          </p>
        </div>
      </div>
    );

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/admin/products", form);
        toast.success("Product created");
      } else {
        await api.put(`/admin/products/${editing.id}`, form);
        toast.success("Product updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-100">
      <div className="border-b border-zinc-900 bg-white px-6 lg:px-10 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="tag">ADMIN CONSOLE</div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight mt-1">
              Inventory management
            </h1>
          </div>
          <button
            onClick={() => setEditing("new")}
            data-testid="admin-add-product-button"
            className="bg-[#FF5A00] text-white px-5 py-3 font-mono-data tracking-widest text-sm border-2 border-zinc-900 hard-shadow-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> ADD PRODUCT
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="px-6 lg:px-10 pt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Package, k: stats.total_products, v: "SKUs" },
            { icon: Users, k: stats.total_users, v: "Users" },
            { icon: ShoppingBag, k: stats.paid_orders + "/" + stats.total_orders, v: "Paid / Total orders" },
            { icon: TrendingUp, k: "₹" + stats.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 }), v: "Revenue" },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`admin-stat-${i}`}
              className="bg-white border border-zinc-900 p-4"
            >
              <s.icon className="w-4 h-4 text-[#FF5A00]" />
              <div className="font-mono-data font-bold text-2xl mt-2">{s.k}</div>
              <div className="tag mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Products table */}
      <div className="px-6 lg:px-10 py-6">
        <div className="bg-white border border-zinc-900 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-products-table">
              <thead className="bg-zinc-100 text-zinc-900 font-mono-data text-xs tracking-widest border-b-2 border-zinc-900">
                <tr>
                  <th className="text-left px-4 py-3">IMG</th>
                  <th className="text-left px-4 py-3">SKU</th>
                  <th className="text-left px-4 py-3">NAME</th>
                  <th className="text-left px-4 py-3">CATEGORY</th>
                  <th className="text-right px-4 py-3">PRICE</th>
                  <th className="text-right px-4 py-3">STOCK</th>
                  <th className="text-right px-4 py-3">ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 font-mono-data text-zinc-500">
                      LOADING...
                    </td>
                  </tr>
                )}
                {products.map((p) => (
                  <tr
                    key={p.id}
                    className="border-t border-zinc-200 hover:bg-zinc-50"
                    data-testid={`admin-row-${p.sku}`}
                  >
                    <td className="px-4 py-3">
                      <img src={p.image} alt="" className="w-12 h-12 object-cover border border-zinc-300" />
                    </td>
                    <td className="px-4 py-3 font-mono-data text-xs">{p.sku}</td>
                    <td className="px-4 py-3 font-medium max-w-xs truncate">{p.name}</td>
                    <td className="px-4 py-3">
                      <span className="tag border border-zinc-300 px-2 py-0.5">
                        {p.category}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono-data">₹{p.price.toFixed(2)}</td>
                    <td className={`px-4 py-3 text-right font-mono-data ${p.stock === 0 ? "text-red-600" : ""}`}>
                      {p.stock.toLocaleString()} {p.unit}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => setEditing(p)}
                        data-testid={`admin-edit-${p.sku}`}
                        className="inline-flex items-center gap-1 border border-zinc-900 px-2 py-1 text-xs font-mono-data hover:bg-zinc-900 hover:text-white mr-1"
                      >
                        <Pencil className="w-3 h-3" /> EDIT
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        data-testid={`admin-delete-${p.sku}`}
                        className="inline-flex items-center gap-1 border border-red-600 text-red-600 px-2 py-1 text-xs font-mono-data hover:bg-red-600 hover:text-white"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {editing && (
        <ProductModal
          initial={editing === "new" ? EMPTY_PRODUCT : editing}
          isNew={editing === "new"}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          saving={saving}
        />
      )}
    </div>
  );
}

function ProductModal({ initial, isNew, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    name: initial.name,
    category: initial.category,
    sku: initial.sku,
    price: initial.price,
    unit: initial.unit,
    stock: initial.stock,
    moq: initial.moq,
    image: initial.image,
    description: initial.description || "",
    specs: initial.specs || {},
  });
  const [specsText, setSpecsText] = useState(
    Object.entries(initial.specs || {})
      .map(([k, v]) => `${k}: ${v}`)
      .join("\n")
  );

  const submit = (e) => {
    e.preventDefault();
    const specs = {};
    specsText.split("\n").forEach((line) => {
      const [k, ...rest] = line.split(":");
      if (k && rest.length) specs[k.trim()] = rest.join(":").trim();
    });
    onSave({
      ...form,
      price: Number(form.price),
      stock: Number(form.stock),
      moq: Number(form.moq),
      specs,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-900/70 flex items-center justify-center p-4">
      <div className="bg-white border-2 border-zinc-900 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white text-zinc-900 border-b-2 border-zinc-900 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="tag text-[#FF5A00]">{isNew ? "NEW PRODUCT" : "EDIT PRODUCT"}</div>
            <div className="font-display font-black text-xl">
              {isNew ? "Add SKU" : initial.sku}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-modal-button"
            className="border-2 border-zinc-900 p-2 hover:bg-zinc-900 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4" data-testid="product-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="tag block mb-1">NAME</label>
              <input
                required
                data-testid="product-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2"
              />
            </div>
            <div>
              <label className="tag block mb-1">SKU</label>
              <input
                required
                data-testid="product-sku-input"
                value={form.sku}
                onChange={(e) => setForm({ ...form, sku: e.target.value.toUpperCase() })}
                disabled={!isNew}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2 font-mono-data disabled:bg-zinc-100"
              />
            </div>
            <div>
              <label className="tag block mb-1">CATEGORY</label>
              <select
                data-testid="product-category-select"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="tag block mb-1">PRICE (₹)</label>
              <input
                required
                type="number"
                step="0.01"
                min="0.01"
                data-testid="product-price-input"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2 font-mono-data"
              />
            </div>
            <div>
              <label className="tag block mb-1">UNIT</label>
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2"
              >
                {["kg", "L", "MT", "pcs"].map((u) => (
                  <option key={u}>{u}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="tag block mb-1">STOCK</label>
              <input
                required
                type="number"
                min="0"
                data-testid="product-stock-input"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2 font-mono-data"
              />
            </div>
            <div>
              <label className="tag block mb-1">MOQ</label>
              <input
                required
                type="number"
                min="1"
                data-testid="product-moq-input"
                value={form.moq}
                onChange={(e) => setForm({ ...form, moq: e.target.value })}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2 font-mono-data"
              />
            </div>
            <div className="col-span-2">
              <label className="tag block mb-1">IMAGE URL</label>
              <input
                required
                data-testid="product-image-input"
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2 font-mono-data text-xs"
              />
              {form.image && (
                <img
                  src={form.image}
                  alt=""
                  className="mt-2 w-32 h-24 object-cover border border-zinc-300"
                  onError={(e) => (e.target.style.display = "none")}
                />
              )}
            </div>
            <div className="col-span-2">
              <label className="tag block mb-1">DESCRIPTION</label>
              <textarea
                data-testid="product-description-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2"
              />
            </div>
            <div className="col-span-2">
              <label className="tag block mb-1">SPECS (one per line: key: value)</label>
              <textarea
                data-testid="product-specs-input"
                value={specsText}
                onChange={(e) => setSpecsText(e.target.value)}
                rows={4}
                placeholder="MFI: 0.3 g/10min&#10;Density: 0.946 g/cc"
                className="w-full border-2 border-zinc-300 focus:border-[#FF5A00] outline-none px-3 py-2 font-mono-data text-xs"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
            <button
              type="button"
              onClick={onClose}
              className="border-2 border-zinc-900 px-5 py-2 font-mono-data text-xs tracking-widest hover:bg-zinc-100"
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="save-product-button"
              className="bg-[#FF5A00] text-white px-5 py-2 font-mono-data text-xs tracking-widest border-2 border-zinc-900 hard-shadow-sm disabled:opacity-50 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? "SAVING..." : isNew ? "CREATE" : "UPDATE"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
